import React, { memo, useCallback, useMemo } from 'react'
import type {
  PartnerInvoice,
  PendingInvoiceAction,
  PendingPaymentReleaseAction,
} from 'src/interfaces/invoice'
import { InvoiceActionVerb } from 'src/constants/invoice/enums'
import { buildInvoiceLabels } from 'src/utils/invoiceLabels'
import { getAvailableInvoiceActions } from 'src/utils/invoiceActions'
import { hasScheduledPaymentRelease } from 'src/utils/invoicePaymentRelease'
import { InvoiceActionsMenu } from './InvoiceActionsMenu'
import { buildPaymentReleaseMenuItems } from './paymentReleaseMenuItems'

interface Props {
  invoice: PartnerInvoice
  /** The parent composed invoice's label, so the modal title reads the same either way. */
  composedInvoiceLabel: string
  onRunAction(action: PendingInvoiceAction): void
  onPaymentReleaseAction(action: PendingPaymentReleaseAction): void
}

const PartnerInvoiceActionsCellComponent = ({
  invoice,
  composedInvoiceLabel,
  onRunAction,
  onPaymentReleaseAction,
}: Props) => {
  const verbs = useMemo(() => {
    return getAvailableInvoiceActions(invoice)
  }, [invoice])

  const paymentReleaseItems = useMemo(() => {
    return buildPaymentReleaseMenuItems(invoice, composedInvoiceLabel, onPaymentReleaseAction)
  }, [invoice, composedInvoiceLabel, onPaymentReleaseAction])

  // A one-invoice run is the same payload shape as a composed run, so it flows through the
  // same modal and the same saga loop.
  const handleSelect = useCallback(
    (verb: InvoiceActionVerb) => {
      onRunAction({
        verb,
        invoiceIds: [invoice.id],
        composedInvoiceLabel,
        invoiceLabels: buildInvoiceLabels([invoice]),
        scheduledReleaseCount: hasScheduledPaymentRelease(invoice) ? 1 : 0,
      })
    },
    [invoice, composedInvoiceLabel, onRunAction],
  )

  return (
    <InvoiceActionsMenu
      verbs={verbs}
      paymentReleaseItems={paymentReleaseItems}
      dataTestId={`partner-invoices-list-actions-${invoice.id}`}
      onSelect={handleSelect}
    />
  )
}

export const PartnerInvoiceActionsCell = memo(PartnerInvoiceActionsCellComponent)
